import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CardComponent } from '@custom-components/general/card/card.component';
import { CardGridComponent } from '@custom-components/general/card-grid';
import { DetailFieldComponent } from '@custom-components/general/detail-field';
import { TablePaginationComponent } from '@custom-components/general/table-pagination/table-pagination.component';
import {
  FilledButtonComponent,
  LinkButtonComponent,
  OutlinedButtonComponent,
  TextButtonComponent,
} from '@custom-components/general/button';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import { filter } from 'rxjs';
import { APP_PATHS, PAGINATION_DEFAULT, XEMEX_OID } from '@core/constants';
import { getUserActivePartner, PartnershipInstallationGroup } from '@core/models';
import { PartnershipStore } from '@root/app/store/partnership.store';
import { AuthStore } from '@root/app/store/auth.store';
import { UserStore } from '@root/app/store/user.store';
import {
  PartnershipActionDialogComponent,
  PartnershipExtendResult,
} from '../../components/partnership-action-dialog/partnership-action-dialog.component';
import {
  AddInstallationGroupDialogComponent,
  AddInstallationGroupDialogData,
} from '../../components/add-installation-group-dialog/add-installation-group-dialog.component';
import {
  RemoveInstallationGroupDialogComponent,
  RemoveInstallationGroupDialogData,
} from '../../components/remove-installation-group-dialog/remove-installation-group-dialog.component';
import { PartnershipTermsDialogComponent } from '../../components/partnership-terms-dialog/partnership-terms-dialog.component';
import { RecentlyVisitedService } from '@core/services';

@Component({
  selector: 'xemex-partnership-detail',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
    PageHeaderComponent,
    PageBodyComponent,
    CardGridComponent,
    CardComponent,
    DetailFieldComponent,
    TablePaginationComponent,
    FilledButtonComponent,
    OutlinedButtonComponent,
    TextButtonComponent,
    LinkButtonComponent,
  ],
  templateUrl: './partnership-detail.component.html',
})
export class PartnershipDetailComponent {
  readonly partnershipStore = inject(PartnershipStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly authStore = inject(AuthStore);
  private readonly userStore = inject(UserStore);
  private readonly recentlyVisited = inject(RecentlyVisitedService);
  private recentlyVisitedRecordedPartnershipOid: string | undefined;

  private readonly partnershipOid = signal('');

  protected readonly pageSize = PAGINATION_DEFAULT.limit;
  protected readonly auditLogPage = signal(1);

  protected readonly breadcrumbs = computed<BreadcrumbItem[]>(() => [
    { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
    {
      label: this.translate.instant('sidebarMenu.partnerships'),
      routerLink: ['/', APP_PATHS.Partnerships],
    },
    { label: this.partnershipStore.detail()?.partnershipType ?? '—' },
  ]);

  /** The user's active partner OID; the all-zero {@link XEMEX_OID} for Xemex super-admins. */
  private readonly activePartnerOid = computed(
    () => getUserActivePartner(this.userStore.user())?.partnerOid,
  );

  /** Enabled feature names for the user's active partner (or via Xemex). */
  private readonly features = computed(() => {
    const partnerOid = this.activePartnerOid();
    return (
      this.authStore
        .permissions()
        ?.filter(p => p.groupObjectId == partnerOid || p.groupObjectId == XEMEX_OID)
        .flatMap(p => p.roles.flatMap(role => role.features.flatMap(f => f.features)))
        .filter(f => f.featureEnabled)
        .map(f => f.featureName) ?? []
    );
  });

  /** True when the user holds the manage_partnerships feature for their partner (or via Xemex). */
  private readonly canManage = computed(() => this.features().includes('manage_partnerships'));

  /** True when the user holds the view_audit_log feature for their partner (or via Xemex). */
  protected readonly canViewAuditLog = computed(() => this.features().includes('view_audit_log'));

  protected readonly auditLogCount = computed(() => this.partnershipStore.auditLogCount());
  protected readonly auditLogTotalPages = computed(() =>
    Math.max(1, Math.ceil(this.auditLogCount() / this.pageSize)),
  );

  private readonly isXemex = computed(() => this.activePartnerOid() === XEMEX_OID);
  /** Caller acts as the partnership owner (its partner owns it, or it's Xemex). */
  private readonly isOwner = computed(
    () =>
      this.isXemex() || this.partnershipStore.detail()?.ownerPartnerOid === this.activePartnerOid(),
  );
  /** Caller acts as the guest (its partner is the guest, or it's Xemex). */
  private readonly isGuest = computed(
    () =>
      this.isXemex() || this.partnershipStore.detail()?.guestPartnerOid === this.activePartnerOid(),
  );
  private readonly status = computed(() => this.partnershipStore.detail()?.status);

  // Action availability mirrors the backend's role + status preconditions.
  protected readonly canAccept = computed(
    () => this.canManage() && this.isGuest() && this.status() === 'Pending',
  );
  protected readonly canDecline = computed(
    () => this.canManage() && this.isGuest() && this.status() === 'Pending',
  );
  protected readonly canExtend = computed(
    () => this.canManage() && this.isOwner() && this.status() === 'Active',
  );
  protected readonly canRevoke = computed(
    () =>
      this.canManage() &&
      this.isOwner() &&
      (this.status() === 'Pending' || this.status() === 'Active'),
  );

  /**
   * True when the user may attach/detach installation groups: they manage partnerships and act as
   * the owner (the owner's groups are the only valid attachment targets). Limited to live
   * partnerships (Pending or Active) — a terminated one shares nothing.
   */
  protected readonly canManageInstallationGroups = computed(
    () =>
      this.canManage() &&
      this.isOwner() &&
      (this.status() === 'Pending' || this.status() === 'Active'),
  );

  constructor() {
    this.route.params.subscribe(params => {
      const partnershipOid = params['partnershipOid'];
      if (partnershipOid) {
        this.partnershipOid.set(partnershipOid);
        this.auditLogPage.set(1);
        this.partnershipStore.getPartnership(partnershipOid);
      }
    });

    // Load the audit-log page reactively once the OID is known and the user is permitted to view it.
    effect(() => {
      const oid = this.partnershipOid();
      const page = this.auditLogPage();
      if (oid && this.canViewAuditLog()) {
        this.partnershipStore.getAuditLog({
          partnershipOid: oid,
          limit: this.pageSize,
          offset: page - 1,
        });
      }
    });

    // Record the visit once the partnership's detail (owner/guest names) has actually loaded.
    effect(() => {
      const detail = this.partnershipStore.detail();
      const oid = this.partnershipOid();
      if (detail && oid && this.recentlyVisitedRecordedPartnershipOid !== oid) {
        this.recentlyVisitedRecordedPartnershipOid = oid;
        this.recentlyVisited.record({
          type: 'partnership',
          id: oid,
          icon: 'handshake',
          primary: `${detail.ownerPartnerName} ↔ ${detail.guestPartnerName}`,
          secondary: detail.partnershipType || this.translate.instant('recentlyUsed.types.partnership'),
          route: ['/', APP_PATHS.Partnerships, oid],
        });
      }
    });
  }

  onViewPartner(partnerOid: string | undefined): void {
    if (partnerOid) {
      this.router.navigate(['/', APP_PATHS.Partners, partnerOid]);
    }
  }

  onViewInstallationGroup(installationGroupOid: string): void {
    this.router.navigate(['/', APP_PATHS.InstallationGroups, installationGroupOid]);
  }

  /** Opens the read-only terms and conditions dialog. */
  onViewTerms(): void {
    this.dialog.open(PartnershipTermsDialogComponent, {
      width: '42rem',
      maxWidth: 'calc(100vw - 2rem)',
    });
  }

  onAddInstallationGroup(): void {
    const detail = this.partnershipStore.detail();
    if (!detail) {
      return;
    }
    this.dialog
      .open(AddInstallationGroupDialogComponent, {
        width: '40rem',
        maxWidth: 'calc(100vw - 2rem)',
        data: {
          ownerPartnerOid: detail.ownerPartnerOid,
          existingGroupOids: detail.installationGroups.map(g => g.installationGroupOid),
        } satisfies AddInstallationGroupDialogData,
      })
      .afterClosed()
      .pipe(filter((groupOid): groupOid is string => !!groupOid))
      .subscribe(installationGroupOid => {
        this.partnershipStore.addInstallationGroup({
          partnershipOid: this.partnershipOid(),
          installationGroupOid,
        });
      });
  }

  onRemoveInstallationGroup(group: PartnershipInstallationGroup, event: MouseEvent): void {
    event.stopPropagation();
    this.dialog
      .open(RemoveInstallationGroupDialogComponent, {
        width: '32rem',
        maxWidth: 'calc(100vw - 2rem)',
        data: { groupName: group.name } satisfies RemoveInstallationGroupDialogData,
      })
      .afterClosed()
      .pipe(filter((confirmed): confirmed is true => confirmed === true))
      .subscribe(() => {
        this.partnershipStore.removeInstallationGroup({
          partnershipOid: this.partnershipOid(),
          installationGroupOid: group.installationGroupOid,
        });
      });
  }

  onAuditLogPageChange(page: number): void {
    this.auditLogPage.set(page);
  }

  onAccept(): void {
    this.confirmAction('accept').subscribe(() =>
      this.partnershipStore.acceptPartnership(this.partnershipOid()),
    );
  }

  onDecline(): void {
    this.confirmAction('decline').subscribe(() =>
      this.partnershipStore.declinePartnership(this.partnershipOid()),
    );
  }

  onRevoke(): void {
    this.confirmAction('revoke').subscribe(() =>
      this.partnershipStore.revokePartnership(this.partnershipOid()),
    );
  }

  onExtend(): void {
    this.confirmAction('extend').subscribe(result => {
      const reviewDueDate = (result as PartnershipExtendResult).reviewDueDate;
      this.partnershipStore.extendPartnership({
        partnershipOid: this.partnershipOid(),
        reviewDueDate,
      });
    });
  }

  /** Resolves an installation group's display name from the loaded detail, falling back to a label. */
  protected installationGroupName(installationGroupOid: string): string {
    const group = this.partnershipStore
      .detail()
      ?.installationGroups.find(g => g.installationGroupOid === installationGroupOid);
    return group?.name ?? this.translate.instant('sidebarMenu.installationGroups');
  }

  /** Human-readable label for an audit event type, falling back to a prettified form. */
  protected eventLabel(eventType: string): string {
    const key = `partnerships.audit.events.${eventType}`;
    const translated = this.translate.instant(key);
    if (translated !== key) {
      return translated;
    }
    const words = eventType.toLowerCase().replaceAll('_', ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  /** Opens the confirm/extend dialog; emits only when the user confirms. */
  private confirmAction(action: 'accept' | 'decline' | 'extend' | 'revoke') {
    const detail = this.partnershipStore.detail();
    return this.dialog
      .open(PartnershipActionDialogComponent, {
        width: '32rem',
        maxWidth: 'calc(100vw - 2rem)',
        data: {
          action,
          ownerPartnerName: detail?.ownerPartnerName ?? '',
          guestPartnerName: detail?.guestPartnerName ?? '',
        },
      })
      .afterClosed()
      .pipe(filter((result): result is true | PartnershipExtendResult => !!result));
  }
}
